'use client'
import React, { createContext, useContext, useMemo } from 'react'

import { SessionData } from '@/app/adapters/types'
import { ListingsApiData } from '@/app/service/models/biListings'
import {
	TableState,
	TableActions,
	createInitialState,
} from '../../../app/utilities/tableUtils'
import { useTableData } from '../../hooks/useTableData'
import { FieldEnum } from '@/lib/components/business-intelligence/bi-listings-transactions/DataTable/enums'

interface OffMarketListingsContextProps {
	state: TableState<ListingsApiData>
	actions: TableActions
	fetchData: () => Promise<void>
}

const OffMarketListingsContext = createContext<
	OffMarketListingsContextProps | undefined
>(undefined)

export const OffMarketListingsProvider: React.FC<{
	children: React.ReactNode
	sessionData: SessionData
}> = ({ children, sessionData }) => {
	const offMarketInitialState: TableState<ListingsApiData> =
		createInitialState<ListingsApiData>()
	const { state, actions, fetchData } = useTableData({
		dataSource: 'offMarketListings',
		errorMessage: 'Error fetching off-market listings data:',
		sessionData,
		initialState: {
			...offMarketInitialState,
			paginationModel: { page: 1, pageSize: 5 },
			rowsPerPage: 5,
		},
		defaultSortField: FieldEnum.OffMarketDate,
		defaultSortDirection: 'desc',
	})

	const value = useMemo(
		() => ({
			state,
			actions,
			fetchData,
		}),
		[state, actions, fetchData]
	)

	return (
		<OffMarketListingsContext.Provider value={value}>
			{children}
		</OffMarketListingsContext.Provider>
	)
}

export const useOffMarketListings = (): OffMarketListingsContextProps => {
	const context = useContext(OffMarketListingsContext)
	if (!context) {
		throw new Error(
			'useOffMarketListings must be used within an OffMarketListingsProvider'
		)
	}
	return context
}
